import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';

import { Case } from '../beans/Case';
import { CaseSearch } from '../beans/CaseSearch';
import { Instance } from '../beans/Instance';
import { Lawyer } from '../beans/Lawyer';
import { Belezhka } from '../beans/belezhka';
import { Izpulnitelno } from '../beans/izpulnitelno';
import { Nakazatelno } from '../beans/nakazatelno';
import { Payment } from '../beans/payment';
import { Income } from '../beans/income';
import { urlLinks } from '../utility/url-links';
import { HttpService } from './http.service';
import { StorageService } from './storage.service';

@Injectable()
export class CaseService {

	constructor(
		private http: HttpService
	) { }


	saveCase(caseObj: String, exists: boolean): Observable<any> {
		if (exists) {
			return this.http.post<any>(urlLinks.rest.cases.updateCase, caseObj).pipe(
				tap(resp => this.emitCaseSaved(resp.id))
			);
		} else {
			return this.http.post<Case>(urlLinks.rest.cases.addCase, caseObj)
				.pipe(
					tap(resp => this.emitCaseSaved(resp.id))
				);
		}
	}

	/** Превръща Date или "DD.MM.YYYY" → "YYYY-MM-DD" (без часове, без UTC) */
	toBackendIsoDate(value: any): string {
		if (!value) return null;

		// Ако е Date (от DatePicker)
		if (value instanceof Date) {
			const y = value.getFullYear();
			const m = ('0' + (value.getMonth() + 1)).slice(-2);
			const d = ('0' + value.getDate()).slice(-2);
			return `${y}-${m}-${d}`;
		}

		// Ако е "DD.MM.YYYY"
		if (typeof value === 'string' && value.includes('.')) {
			const [dd, MM, yyyy] = value.split('.');
			return `${yyyy}-${MM}-${dd}`;
		}

		// Ако вече е в ISO "YYYY-MM-DD"
		return value;
	}

	/** Превръща backend ISO "YYYY-MM-DD" → Date (за UI datepicker) */
	fromBackendDate(value: any): Date {
		if (!value) return null;

		if (value instanceof Date) return value;

		// ISO "YYYY-MM-DD"
		if (typeof value === 'string' && value.includes('-')) {
			const [yyyy, MM, dd] = value.split('-');
			return new Date(Number(yyyy), Number(MM) - 1, Number(dd), 12, 0, 0);
		}

		// "DD.MM.YYYY"
		if (typeof value === 'string' && value.includes('.')) {
			const [dd, MM, yyyy] = value.split('.');
			return new Date(Number(yyyy), Number(MM) - 1, Number(dd), 12, 0, 0);
		}

		return null;
	}

	saveIzpalnitelno(obj: Izpulnitelno, lawsuiteId: number): Observable<any> {
		return this.http.post<any>(urlLinks.rest.cases.izpulnitelno.save + lawsuiteId, obj);
	}

	saveNakazatelno(obj: String): Observable<any> {
		return this.http.post<any>(urlLinks.rest.cases.nakazatelno.save, obj);
	}

	getNakazatelnoLawyer(id: number): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.nakazatelno.getOtgovornik + id);
	}

	deleteNakazatelno(id: number): Observable<any> {
		return this.http.post<any>(urlLinks.rest.cases.nakazatelno.remove + id, {});
	}

	/** Чисти "#3331" / " 3331 " / "abc" към number или null. */
	private normalizeId(value: any): number | null {
		if (value === null || value === undefined || value === '') {
			return null;
		}
		const cleaned = String(value).replace(/[^0-9]/g, '');
		return cleaned ? Number(cleaned) : null;
	}

	searchCases(searchObj: CaseSearch, page: number, size: number): Observable<any> {
		if (searchObj.traumaType && searchObj.traumaType.name) {
			searchObj.traumaTypeName = searchObj.traumaType.name;
		}
		const payload = {
			...searchObj,
			id: this.normalizeId(searchObj.id),
			dataPTP: this.toBackendIsoDate(searchObj.dataPTP),
			data_suzd_inst_grdelo_ot: this.toBackendIsoDate(searchObj.data_suzd_inst_grdelo_ot),
			data_suzd_inst_grdelo_do: this.toBackendIsoDate(searchObj.data_suzd_inst_grdelo_do)
		};
		return this.http.post(
			urlLinks.rest.cases.searchCases2 + `?page=${page}&size=${size}`,
			payload
		);
	}

	getCase(lawsuiteId: number): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.load + lawsuiteId);
	}

	getCaseHistory(lawsuiteId: number): Observable<any[]> {
		return this.http.get<any[]>(urlLinks.rest.cases.history + lawsuiteId + '/history');
	}

	getInstanceHistory(instanceId: number): Observable<any[]> {
		return this.http.get<any[]>(urlLinks.rest.cases.instanceHistory + instanceId + '/history');
	}

	deleteCase(caseId: number): Observable<any> {
		return this.http.delete<any>(urlLinks.rest.cases.deleteCase + caseId);
	}

	getNakazatelni(lawsuiteId: number): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.nakazatelno.getLawsuiteNakazatelnoStatuses + lawsuiteId);
	}

	getIncomes(lawsuiteId: number): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.income.list + lawsuiteId);
	}

	addIncome(incomeObj: Income, lawsuiteId: number): Observable<any> {
		incomeObj.lawsuiteId = lawsuiteId;
		return this.http.post<any>(urlLinks.rest.cases.income.add, incomeObj);
	}

	deleteIncome(id: number): Observable<any> {
		return this.http.post<any>(urlLinks.rest.cases.income.remove + id, {});
	}

	getPayments(lawsuiteId: number): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.payment.list + lawsuiteId);
	}

	addPayment(paymentObj: Payment, lawsuiteId: number): Observable<any> {
		paymentObj.lawsuiteId = lawsuiteId;
		return this.http.post<any>(urlLinks.rest.cases.payment.add, paymentObj);
	}

	deletePayment(id: number): Observable<any> {
		return this.http.post<any>(urlLinks.rest.cases.payment.remove + id, {});
	}

	getInstance(lawsuiteId: number): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.instance.list + lawsuiteId);
	}

	addInstance(obj: String): Observable<any> {
		// Добавянето на инстанция може да затвори делото автоматично (архивен
		// статус → prikljucheno). Затова след успех емитваме caseSaved$ — така
		// и отвореният edit-модал, и списъкът се обновяват (сивата точка).
		return this.http.post<Instance>(urlLinks.rest.cases.instance.add, obj).pipe(
			tap((resp: any) => { if (resp && resp.lawsuiteId) { this.emitCaseSaved(resp.lawsuiteId); } })
		);
	}

	deleteInstance(id: number): Observable<any> {
		return this.http.post<any>(urlLinks.rest.cases.instance.remove + id, {});
	}

	getNotes(lawsuiteId: number): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.belezhka.list + lawsuiteId);
	}

	addNote(obj: Belezhka, lawsuiteId: number): Observable<any> {
		obj.lawsuiteId = lawsuiteId;
		return this.http.post<any>(urlLinks.rest.cases.belezhka.add, obj);
	}

	deleteNote(id: number): Observable<any> {
		return this.http.post<any>(urlLinks.rest.cases.belezhka.remove + id, {});
	}

	getOtgovorniciNakazatelno(): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.nakazatelno.otgovornici);
	}

	changeOtgovornikNakazatelno(id: number, obj: Lawyer): Observable<any> {
		return this.http.post<any>(urlLinks.rest.cases.nakazatelno.changeotgovornik + id, obj);
	}

	getNasrocheniDelaByUser(isExtended: boolean): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.nasrocheniDela + '?isExtended=' + isExtended);
	}

	getCalendarNotifications(isExtended: boolean): Observable<any> {
		return this.http.get<any>(environment.serviceBaseUrl + '/api/notifications/calendar?isExtended=' + isExtended);
	}

	deactivateNotification(notificationId: number): Observable<any> {
		return this.http.post<any>(environment.serviceBaseUrl + '/api/notifications/' + notificationId + '/deactivate', {});
	}

	getCaseNumberSuggestions(term: string): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.caseNumbers + '?q=' + encodeURIComponent(term));
	}

	getDamageNumberSuggestions(term: string): Observable<any> {
		return this.http.get<any>(urlLinks.rest.cases.damageNumbers + '?q=' + encodeURIComponent(term));
	}

	formatDate(dateToFormat): Date {
		if (dateToFormat) {
			if (typeof dateToFormat === 'string' || dateToFormat instanceof String) {
				let dateParts: string[] = dateToFormat.split('.');
				//month is 0-based, that's why we need dataParts[1] - 1
				return new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]), 12, 0, 0, 0);
			}
			return dateToFormat;
		}
		return;
	}

	/** emitvame save event  */
	private caseSavedSource = new Subject<number>();
	caseSaved$ = this.caseSavedSource.asObservable();

	/** puskame subitie che e saved delo - kum vsichki zainteresovani */
	emitCaseSaved(caseId: number) {
		this.caseSavedSource.next(caseId);
	}
} 
